import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { useAuth } from "@/lib/auth/AuthProvider";
import { useEmergencyConfig } from "@/lib/safety/useEmergencyConfig";
import { useSafety } from "@/lib/safety/useSafety";
import { resolveEmergencyNumber } from "@/lib/safety/types";

const POSITION_CLASSES: Record<string, string> = {
  "bottom-right": "bottom-6 right-6",
  "bottom-left": "bottom-6 left-6",
  "top-right": "top-20 right-6",
  "top-left": "top-20 left-6",
};

// Routes where the floating button MUST NOT appear (auth flow only).
// Per spec: button is always visible on every member screen; visibility is not configurable.
const HIDE_ROUTES = ["/auth", "/reset-password"];

// Window between presses for Press 1 → Press 2 to register as a "double" (ms).
const SECOND_PRESS_WINDOW_MS = 4000;

/**
 * Emergency Button — two-press behavior, NEVER shows a "Call 911" dialog.
 *
 * Press 1 (silent): contact emergency contact, start auto-recording, share GPS.
 *   - Optional member-authored confirmation message displays briefly (if enabled).
 *   - No cancel/undo. Always fires immediately.
 *
 * Press 2 (within 4s of Press 1): instant locale-aware emergency dial.
 *   - 911 US, 999 UK, 112 EU, 000 AU, etc.
 *   - NEVER shows a confirmation message — instant + silent.
 *   - No cancel/undo.
 */
const EmergencyButton = () => {
  const { session } = useAuth();
  const { config } = useEmergencyConfig();
  const { pressEmergency } = useSafety();
  const { pathname } = useLocation();

  const [pressStage, setPressStage] = useState<0 | 1>(0);
  const [confirmFlash, setConfirmFlash] = useState<string | null>(null);
  const resetTimer = useRef<number | null>(null);
  const flashTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    };
  }, []);

  if (!session) return null;
  if (!config?.enabled) return null;
  if (HIDE_ROUTES.some((r) => pathname.startsWith(r))) return null;

  const handlePress = async () => {
    // PRESS 2 — instant locale-aware dial. Silent. No message. No undo.
    if (pressStage === 1) {
      if (resetTimer.current) {
        window.clearTimeout(resetTimer.current);
        resetTimer.current = null;
      }
      setPressStage(0);
      setConfirmFlash(null);

      const locale = typeof navigator !== "undefined" ? navigator.language : null;
      const number = resolveEmergencyNumber(locale);
      // Fire-and-forget log of the dial event in the background.
      void pressEmergency({ stage: 2 });
      // Trigger the device's native dialer immediately.
      if (typeof window !== "undefined") {
        window.location.href = `tel:${number}`;
      }
      return;
    }

    // PRESS 1 — silent: contact + record + GPS. Optional confirmation flash.
    setPressStage(1);
    void pressEmergency({ stage: 1 });

    // Optional member-written confirmation message (max 80 chars).
    // Stored on config; shown briefly only if member opted in during onboarding.
    const confirmEnabled = (config as any)?.confirm_enabled;
    const confirmMessage = (config as any)?.confirm_message as string | undefined;
    if (confirmEnabled && confirmMessage) {
      setConfirmFlash(confirmMessage.slice(0, 80));
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
      flashTimer.current = window.setTimeout(() => setConfirmFlash(null), 2200);
    }

    // Reset to stage 0 after the second-press window elapses.
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setPressStage(0);
    }, SECOND_PRESS_WINDOW_MS);
  };

  return (
    <>
      <button
        type="button"
        onClick={handlePress}
        aria-label={config.label || "Emergency"}
        title={config.label || "Emergency"}
        className={`fixed z-50 ${POSITION_CLASSES[config.position] ?? POSITION_CLASSES["bottom-right"]} h-14 w-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95 ${pressStage === 1 ? "ring-4 ring-offset-2 ring-offset-background animate-pulse" : ""}`}
        style={{
          backgroundColor: `hsl(${config.color_hsl})`,
          color: "hsl(var(--primary-foreground))",
          // @ts-expect-error CSS var
          "--tw-ring-color": `hsl(${config.color_hsl})`,
        }}
      >
        <ShieldAlert className="h-6 w-6" />
      </button>

      {/* Optional, member-authored confirmation flash for Press 1 only. */}
      {confirmFlash && (
        <div
          role="status"
          aria-live="polite"
          className={`fixed z-50 ${POSITION_CLASSES[config.position] ?? POSITION_CLASSES["bottom-right"]} mb-20 pointer-events-none`}
          style={{ transform: "translateY(-72px)" }}
        >
          <div
            className="rounded-full px-3 py-1.5 text-xs font-medium shadow-lg"
            style={{
              backgroundColor: `hsl(${config.color_hsl})`,
              color: "hsl(var(--primary-foreground))",
            }}
          >
            {confirmFlash}
          </div>
        </div>
      )}
    </>
  );
};

export default EmergencyButton;
